/**
 * Shared helpers for the login-activity views on /user-management and
 * /staff-access.
 *
 * The backend sends every timestamp as an ISO-8601 UTC instant
 * ("2026-09-09T06:12:44Z"). Formatting to IST happens here, in one place, so
 * the two screens cannot drift. Note this is deliberately NOT the `fmtDate`
 * used elsewhere in the dashboard, which splits on 'T' and throws the time
 * away — login *times* are the point of this feature.
 */

export interface UserSession {
  session_id: string;
  app_name?: string;
  platform?: string;
  device_model?: string;
  app_version?: string | null;
  ip?: string | null;
  login_at?: string;
  logout_at?: string | null;
  logout_reason?: string | null;
  status?: string;
}

const IST_OFFSET_MINUTES = 5 * 60 + 30;

const parse = (v?: string | null): Date | null => {
  if (!v) return null;
  const dt = new Date(v);
  return isNaN(dt.getTime()) ? null : dt;
};

const pad = (n: number) => String(n).padStart(2, '0');

/** 'dd-mm-yyyy HH:MM:SS' in IST, or '—'. */
export const fmtDateTimeIST = (v?: string | null): string => {
  const dt = parse(v);
  if (!dt) return '—';
  // Shift the instant by the IST offset and read it back in UTC, so the
  // result is IST regardless of where the viewer's browser is.
  const ist = new Date(dt.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
  return (
    `${pad(ist.getUTCDate())}-${pad(ist.getUTCMonth() + 1)}-${ist.getUTCFullYear()} ` +
    `${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}:${pad(ist.getUTCSeconds())}`
  );
};

/** 'just now' | '15m ago' | '3h ago' | '4d ago' | '—'. */
export const fmtRelative = (v?: string | null, now: Date = new Date()): string => {
  const dt = parse(v);
  if (!dt) return '—';
  const seconds = Math.floor((now.getTime() - dt.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export type StatusTone = 'active' | 'closed' | 'stale';

/** Display text + tone for a backend session status. */
export const statusLabel = (status?: string): { text: string; tone: StatusTone } => {
  switch (status) {
    case 'ACTIVE':
      return { text: 'Still logged in', tone: 'active' };
    case 'LOGGED_OUT':
      return { text: 'Logged out', tone: 'closed' };
    case 'SUPERSEDED':
      return { text: 'New login', tone: 'closed' };
    case 'EXPIRED':
      return { text: 'Expired', tone: 'stale' };
    default:
      return { text: '—', tone: 'stale' };
  }
};

export const APP_LABELS: Record<string, string> = {
  animalkart: 'Mobile app',
  animalkart_dashboard: 'Dashboard',
};

export const appLabel = (app?: string): string => APP_LABELS[app || ''] || app || '—';
